import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  ConversationProvider,
  useConversationControls,
  useConversationStatus,
} from "@elevenlabs/react";
import { useLocation, useNavigate } from "react-router-dom";
import { MessageCircle, Minus, Send, ThumbsDown, ThumbsUp, X } from "lucide-react";
import { LOGO_ICON_SRC } from "../components/reusables/Logo";
import { createClientTools } from "./clientTools";
import {
  buildDynamicVariables,
  buildSessionExtrasForEntryIntent,
  isCreateRoutePath,
  isEditRoutePath,
  isOrgOnTrial,
} from "./orgHelpers";
import { describeDashboardPath } from "./navigation";
import { useAssistantUiBridge } from "./AssistantUiBridgeContext";
import {
  captureAssistantEvent,
  messageProps,
  sendElevenLabsOverallFeedback,
  summarizeToolResult,
} from "./analytics";
import {
  buildAssistantSessionOptions,
  getAssistantPreviewBranchId,
} from "./sessionOptions";
import {
  fetchAssistantAvailable,
  installAssistantAvailabilityDebugHelpers,
  isCreditsExhausted,
} from "./availability";

const STORAGE_KEY = "twilanswer_onboarding_assistant_hidden";
const STATUS_EVENT = "twilanswer-assistant-status";
const MESSAGE_EVENT = "twilanswer-assistant-message";
const EDIT_ROUTE_DWELL_MS = 60_000;
const OnboardingAssistantContext = createContext(null);

const TOOL_STATUS_LABELS = {
  appClient_fetchOrgInfo: "Looking up your organization…",
  appClient_fetchOrgRecordings: "Checking recordings…",
  appClient_fetchOrgForwardingNumbers: "Checking forwarding numbers…",
  appClient_fetchIncomingPhoneNumbers: "Checking Twilio numbers…",
  appClient_fetchOrgRoutes: "Checking your call routes…",
  appClient_fetchRouteConfig: "Reading the route config…",
  appClient_fetchOrgChannels: "Checking channels…",
  appClient_fetchCallsInfo: "Fetching recent calls…",
  appClient_promptAddForwardingNumber: "Opening add forwarding number…",
  appClient_promptAddRecording: "Opening add recording…",
  appClient_promptAddChannel: "Opening add channel…",
  appClient_promptContactSupport: "Opening contact support…",
  appClient_updateCurrentRouteConfig: "Updating the route draft…",
  appClient_navigateTo: "Navigating…",
  appClient_inviteUser: "Opening invite user…",
  appClient_controlDialer: "Opening the dialer…",
};

function emitAssistantStatus(detail) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STATUS_EVENT, { detail }));
}

function emitSystemIndicator(text) {
  if (typeof window === "undefined" || !text) return;
  window.dispatchEvent(
    new CustomEvent(MESSAGE_EVENT, {
      detail: { role: "system", text },
    })
  );
}

/** Short chat divider label for app → agent context nudges (not shown as a user bubble). */
function formatSystemIndicator(event) {
  if (!event?.type) return null;
  switch (event.type) {
    case "navigation": {
      const label = describeDashboardPath(event.path).page_label;
      return label ? `Navigated to ${label}` : "Page changed";
    }
    case "channel_added":
      return event.channelValue
        ? `Channel added · ${event.channelValue}`
        : "Channel added";
    case "recording_added":
      return "Recording saved";
    case "route_saved":
      return "Route saved";
    case "user_invited":
      return "User invited";
    case "support_sent":
      return "Support message sent";
    case "call_ended":
      return "Call ended";
    case "call_details_opened":
      return "Opened call details";
    case "post_onboarding_inbound":
      return "Inbound setup";
    case "error":
      return event.message ? `Error · ${event.message}` : "Something went wrong";
    default:
      return null;
  }
}

function wrapClientToolsWithStatus(tools) {
  if (!tools || typeof tools !== "object") return tools;
  const wrapped = {};
  for (const [name, fn] of Object.entries(tools)) {
    if (typeof fn !== "function") {
      wrapped[name] = fn;
      continue;
    }
    wrapped[name] = async (...args) => {
      emitAssistantStatus({
        phase: "tool",
        label: TOOL_STATUS_LABELS[name] || `Running ${name}…`,
        tool: name,
      });
      const started = Date.now();
      try {
        const result = await fn(...args);
        const summary = summarizeToolResult(result);
        captureAssistantEvent("assistant_tool_called", {
          tool: name,
          duration_ms: Date.now() - started,
          ...summary,
        });
        if (name === "appClient_navigateTo" && summary.ok) {
          captureAssistantEvent("assistant_navigated", {
            path: summary.path || null,
          });
        }
        if (name === "appClient_updateCurrentRouteConfig" && summary.ok) {
          captureAssistantEvent("assistant_route_drafted", {
            primary_action: summary.primary_action || null,
            has_ivr_menu: Boolean(summary.has_ivr_menu),
            applied: summary.applied !== false,
          });
        }
        return result;
      } catch (err) {
        captureAssistantEvent("assistant_tool_called", {
          tool: name,
          ok: false,
          duration_ms: Date.now() - started,
          error: err?.message || String(err),
        });
        throw err;
      } finally {
        emitAssistantStatus({ phase: "thinking", label: "Thinking…" });
      }
    };
  }
  return wrapped;
}

function isAssistantEnabled() {
  return process.env.REACT_APP_ELEVENLABS_ASSISTANT_ENABLED === "true";
}

function FloatingLauncherButton({ onClick }) {
  const [entered, setEntered] = useState(false);

  useEffect(() => {
    const id = setTimeout(() => setEntered(true), 1000);
    return () => clearTimeout(id);
  }, []);

  return (
    <button
      type="button"
      aria-label="Need a Hand?"
      onClick={onClick}
      className={`fixed bottom-4 right-4 z-[1390] flex items-center gap-2 rounded-full bg-brand-primary text-white px-4 py-2.5 shadow-lg hover:bg-brand-hover text-sm font-medium transition-all duration-300 ease-out ${
        entered
          ? "translate-y-0 opacity-100"
          : "translate-y-4 opacity-0 pointer-events-none"
      }`}
    >
      <MessageCircle className="w-4 h-4" />
      Need a Hand?
    </button>
  );
}

function UnavailablePanel({ open, onClose, creditStatus }) {
  const bridge = useAssistantUiBridge();

  if (!open) return null;

  const handleContactSupport = () => {
    captureAssistantEvent("assistant_unavailable_contact_support", {
      credit_status: creditStatus || "unknown",
    });
    onClose?.();
    if (bridge?.openSupportComposeModal) {
      bridge.openSupportComposeModal({
        subject: "Help with Twilanswer setup",
        body: "Hi Alex — I could use a hand with:\n\n",
      });
    } else {
      window.location.href =
        "mailto:hello@twilanswer.com?subject=Help%20with%20Twilanswer%20setup&body=Hi%20Alex%20%E2%80%94%20I%20could%20use%20a%20hand%20with%3A%0A%0A";
    }
  };

  return (
    <div className="fixed bottom-20 right-4 z-[1400] w-[min(100vw-2rem,380px)] flex flex-col rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-gray-900">Setup assistant</div>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
            Beta
          </span>
        </div>
        <button
          type="button"
          aria-label="Close"
          className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
          onClick={onClose}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="px-4 py-5 space-y-4">
        <div className="flex items-start gap-2.5">
          <div
            className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-mist"
            aria-hidden
          >
            <img
              src={LOGO_ICON_SRC}
              alt=""
              className="h-3.5 w-3.5 object-contain"
            />
          </div>
          <div className="min-w-0 space-y-2">
            <p className="text-sm leading-relaxed text-gray-900">
              Our AI Setup Assistant isn&apos;t available right now.
            </p>
            <p className="text-sm leading-relaxed text-gray-600">
              You can keep setting things up in the dashboard, or contact support
              and we&apos;ll help you directly.
            </p>
          </div>
        </div>

        <button
          type="button"
          onClick={handleContactSupport}
          className="w-full rounded-lg bg-brand-primary px-3 py-2.5 text-sm font-medium text-white hover:bg-brand-hover"
        >
          Contact support
        </button>
      </div>
    </div>
  );
}

function formatAssistantEvent(event) {
  if (!event?.type) return null;
  switch (event.type) {
    case "navigation":
      return `User navigated to ${event.path || "another page"}. Consider for next response, but don't react only to this update.`;
    case "panel_opened": {
      const path = event.path || "another page";
      const label = event.pageLabel ? ` (${event.pageLabel})` : "";
      return (
        `Assistant panel opened. User is currently on ${path}${label}. ` +
        `Use this as the current page context. If this is a route editor, consider appClient_fetchRouteConfig before changing anything.`
      );
    }
    case "channel_added":
      return (
        `[App update] A channel was just added` +
        `${event.channelValue ? ` (${event.channelValue})` : ""}. ` +
        `Acknowledge briefly and continue the setup immediately. Do not ask the user to confirm.`
      );
    case "recording_added":
      return (
        `[App update] A recording was just saved successfully` +
        `${event.recordingId ? ` (id ${event.recordingId})` : ""}. ` +
        `Acknowledge briefly, then continue the setup immediately. ` +
        `If drafting send_to_vm or play_recording, set primaryRecording to that id. ` +
        `Do not ask the user to confirm they finished.`
      );
    case "route_saved":
      return `Status: user saved the route${
        event.routeId ? ` (id ${event.routeId})` : ""
      }.`;
    case "call_ended": {
      const secs = event.durationSeconds != null
        ? Math.round(Number(event.durationSeconds))
        : null;
      return (
        `[App update] Outbound call just ended.` +
        `${secs != null ? ` Duration ~${secs}s.` : ""}` +
        ` Recording ${event.recordingEnabled ? "was enabled" : "was disabled"}.` +
        `${
          event.transcriptionEnabled != null
            ? ` Transcription ${event.transcriptionEnabled ? "on" : "off"}.`
            : ""
        }` +
        ` Acknowledge briefly and continue setup (e.g. offer call history). Do not ask the user to confirm the call ended.`
      );
    }
    case "call_details_opened":
      return `User opened call details${
        event.callId ? ` for call ${event.callId}` : ""
      }.`;
    case "user_invited":
      return "Status: user completed the invite-user flow.";
    case "support_sent":
      return (
        `[App update] User sent a support message` +
        `${event.subject ? ` (subject: ${event.subject})` : ""}. ` +
        `Acknowledge briefly that it was sent to Alex and continue helping if needed.`
      );
    case "post_onboarding_inbound":
      return (
        `[App update] User just finished Twilio setup and chose to set up inbound call routing first. ` +
        `They are on the create-route editor. Your first message already covered the greeting. ` +
        `When you next respond, help them create and configure their first inbound call route immediately. ` +
        `Do not ask what they would like to do.`
      );
    case "error":
      return `Error: ${event.message || "Something went wrong in the app."}`;
    default:
      return `Status update: ${event.type}`;
  }
}

function ChatPanelInner({
  open,
  onClose,
  onMinimize,
  onConversationFailure,
  onPanelReady,
  consumeSessionExtras,
}) {
  const { startSession, endSession, sendUserMessage, getId } =
    useConversationControls();
  const { status } = useConversationStatus();
  const location = useLocation();
  const [input, setInput] = useState("");
  const [messages, setMessages] = useState([]);
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState(null);
  const [activity, setActivity] = useState(null); // { phase, label }
  const [sessionFeedback, setSessionFeedback] = useState(null); // 'up' | 'down' | null
  const listRef = useRef(null);
  const latestMessageRef = useRef(null);
  const sessionStartedRef = useRef(false);
  const lastAgentTextRef = useRef("");
  const conversationIdRef = useRef(null);
  const messageIdRef = useRef(0);
  const statusErrorReportedRef = useRef(false);
  const sessionExtrasRef = useRef(null);
  const agentId = process.env.REACT_APP_ELEVENLABS_AGENT_ID;

  const nextMessageId = () => {
    messageIdRef.current += 1;
    return `m${messageIdRef.current}`;
  };

  const refreshConversationId = useCallback(() => {
    try {
      const id = typeof getId === "function" ? getId() : null;
      if (id) conversationIdRef.current = id;
      return id || conversationIdRef.current;
    } catch {
      return conversationIdRef.current;
    }
  }, [getId]);

  useEffect(() => {
    if (status === "connected") {
      refreshConversationId();
    }
  }, [status, refreshConversationId]);

  // When the panel opens (or finishes connecting), tell the agent the current page.
  // Path changes while open are handled separately via navigation contextual updates.
  useEffect(() => {
    if (!open || status !== "connected") return;
    const extras = sessionExtrasRef.current;
    onPanelReady?.({
      path: location.pathname,
      skipPanelOpenedNudge: Boolean(extras?.skipPanelOpenedNudge),
      skipActionableNudge: Boolean(extras?.skipActionableNudge),
    });
    // intentionally omit location.pathname — reopen/connect only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, status, onPanelReady]);

  useEffect(() => {
    if (!open || !agentId) return;
    if (status === "connected" || status === "connecting") return;
    if (sessionStartedRef.current) return;

    sessionStartedRef.current = true;
    setConnecting(true);
    setError(null);
    try {
      const extras =
        typeof consumeSessionExtras === "function"
          ? consumeSessionExtras()
          : null;
      sessionExtrasRef.current = extras;

      const dyn = buildDynamicVariables({
        pathname: location.pathname,
        entryIntent: extras?.entryIntent || undefined,
      });
      const overrides = extras?.firstMessageOverride
        ? { agent: { firstMessage: extras.firstMessageOverride } }
        : undefined;

      startSession(
        buildAssistantSessionOptions({
          agentId,
          dynamicVariables: dyn,
          overrides,
        })
      );
      captureAssistantEvent("assistant_opened", {
        preview_branch: Boolean(getAssistantPreviewBranchId()),
        entry_intent: extras?.entryIntent || null,
      });
    } catch (e) {
      sessionStartedRef.current = false;
      sessionExtrasRef.current = null;
      setError(e?.message || "Could not start assistant");
      setConnecting(false);
      onConversationFailure?.({
        where: "start_session",
        message: e?.message || "Could not start assistant",
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, agentId]);

  useEffect(() => {
    if (!open) {
      sessionStartedRef.current = false;
      sessionExtrasRef.current = null;
    }
  }, [open]);

  useEffect(() => {
    if (status === "connected" || status === "disconnected" || status === "error") {
      setConnecting(false);
    }
    if (status === "disconnected" || status === "error") {
      sessionStartedRef.current = false;
    }
    if (status === "connected") {
      statusErrorReportedRef.current = false;
    }
    if (status === "error" && open && !statusErrorReportedRef.current) {
      statusErrorReportedRef.current = true;
      onConversationFailure?.({
        where: "conversation_status",
        message: "ElevenLabs conversation status error",
      });
    }
  }, [status, open, onConversationFailure]);

  useEffect(() => {
    const container = listRef.current;
    const target = latestMessageRef.current;
    if (!container || !target) return;
    // Align the top of the newest message with the top of the chat viewport
    // (so long agent replies are readable from the start instead of jumping to the end).
    const nextTop =
      container.scrollTop +
      (target.getBoundingClientRect().top - container.getBoundingClientRect().top);
    container.scrollTop = Math.max(0, nextTop);
  }, [messages, open]);

  const handleSend = async (e) => {
    e?.preventDefault?.();
    const text = input.trim();
    if (!text || status !== "connected") return;
    lastAgentTextRef.current = "";
    setActivity({ phase: "thinking", label: "Thinking…" });
    setMessages((prev) => [...prev, { id: nextMessageId(), role: "user", text }]);
    setInput("");
    captureAssistantEvent("assistant_message_sent", {
      role: "user",
      conversation_id: refreshConversationId(),
      ...messageProps(text),
    });
    try {
      sendUserMessage(text);
    } catch (err) {
      setActivity(null);
      setError(err?.message || "Failed to send");
      captureAssistantEvent("assistant_error", {
        message: err?.message || "Failed to send",
        where: "send_message",
        conversation_id: conversationIdRef.current,
      });
    }
  };

  const handleEnd = async () => {
    const conversationId = refreshConversationId();
    captureAssistantEvent("assistant_closed", {
      message_count: messages.length,
      conversation_id: conversationId,
      session_feedback: sessionFeedback,
    });
    try {
      await endSession();
    } catch {
      /* ignore */
    }
    sessionStartedRef.current = false;
    lastAgentTextRef.current = "";
    conversationIdRef.current = null;
    setMessages([]);
    setSessionFeedback(null);
    onClose?.();
  };

  const handleMinimize = () => {
    captureAssistantEvent("assistant_minimized", {
      message_count: messages.length,
      conversation_id: refreshConversationId(),
    });
    onMinimize?.();
  };

  const handleSessionFeedback = (rating) => {
    if (rating !== "up" && rating !== "down") return;
    if (sessionFeedback === rating) return;
    const like = rating === "up";
    const conversationId = refreshConversationId();
    setSessionFeedback(rating);
    captureAssistantEvent("assistant_feedback", {
      rating,
      previous_rating: sessionFeedback,
      scope: "conversation",
      message_count: messages.length,
      conversation_id: conversationId,
      elevenlabs_sent: Boolean(conversationId),
    });
    sendElevenLabsOverallFeedback(conversationId, like);
  };

  // Use complete onMessage only — streaming + final message caused duplicate bubbles.
  useEffect(() => {
    const onChatEvent = (ev) => {
      const role = ev.detail?.role || "agent";
      const text = (ev.detail?.text || "").trim();
      if (!text) return;

      if (role === "system") {
        setMessages((prev) => [
          ...prev,
          { id: nextMessageId(), role: "system", text },
        ]);
        return;
      }

      if (text === lastAgentTextRef.current) return;
      lastAgentTextRef.current = text;
      setActivity(null);
      const eventId = ev.detail?.eventId ?? ev.detail?.event_id ?? null;
      captureAssistantEvent("assistant_message_received", {
        role: "agent",
        event_id: eventId,
        conversation_id: conversationIdRef.current,
        ...messageProps(text),
      });
      setMessages((prev) => {
        const last = prev[prev.length - 1];
        if (last?.role === "agent" && last.text.trim() === text) {
          return prev;
        }
        return [...prev, { id: nextMessageId(), role: "agent", text }];
      });
    };
    const onStatus = (ev) => {
      const detail = ev.detail;
      if (!detail?.phase || detail.phase === "idle") {
        setActivity(null);
        return;
      }
      setActivity({
        phase: detail.phase,
        label: detail.label || "Working…",
      });
    };
    window.addEventListener(MESSAGE_EVENT, onChatEvent);
    window.addEventListener(STATUS_EVENT, onStatus);
    return () => {
      window.removeEventListener(MESSAGE_EVENT, onChatEvent);
      window.removeEventListener(STATUS_EVENT, onStatus);
    };
  }, []);

  if (!open) return null;

  return (
    <div className="fixed bottom-20 right-4 z-[1400] w-[min(100vw-2rem,380px)] h-[min(70vh,520px)] flex flex-col rounded-xl border border-gray-200 bg-white shadow-xl overflow-hidden">
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-gray-100 bg-slate-50">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold text-gray-900">Setup assistant</div>
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700">
            Beta
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button
            type="button"
            aria-label="Minimize"
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
            onClick={handleMinimize}
          >
            <Minus className="w-4 h-4" />
          </button>
          <button
            type="button"
            aria-label="Close"
            className="p-1.5 rounded-md text-gray-500 hover:bg-gray-100"
            onClick={handleEnd}
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      <div ref={listRef} className="flex-1 overflow-y-auto px-3 py-4 space-y-4 bg-white">
        {!agentId && (
          <div className="text-xs text-amber-700 bg-amber-50 rounded-md p-2">
            Missing REACT_APP_ELEVENLABS_AGENT_ID. Add it after pushing the agent via the ElevenLabs CLI.
          </div>
        )}
        {connecting && (
          <div className="text-xs text-gray-500">Connecting…</div>
        )}
        {error && (
          <div className="text-xs text-red-600 bg-red-50 rounded-md p-2">{error}</div>
        )}
        {messages.length === 0 && status === "connected" && (
          <div className="text-xs text-gray-400 pl-9">Say hi to get started.</div>
        )}
        {messages.map((m, i) => {
          const isLatest = i === messages.length - 1;
          if (m.role === "system") {
            return (
              <div
                key={m.id || `system-${i}`}
                ref={isLatest ? latestMessageRef : undefined}
                className="flex items-center gap-2 py-1"
                role="status"
              >
                <div className="flex-1 h-px bg-gray-100" />
                <span className="shrink-0 text-[11px] text-gray-400 font-medium tracking-wide">
                  {m.text}
                </span>
                <div className="flex-1 h-px bg-gray-100" />
              </div>
            );
          }

          if (m.role === "user") {
            return (
              <div
                key={m.id || `user-${i}`}
                ref={isLatest ? latestMessageRef : undefined}
                className="ml-auto max-w-[85%] rounded-2xl bg-gray-100 px-3.5 py-2.5 text-sm leading-relaxed text-gray-900 whitespace-pre-wrap"
              >
                {m.text}
              </div>
            );
          }

          return (
            <div
              key={m.id || `agent-${i}`}
              ref={isLatest ? latestMessageRef : undefined}
              className="flex items-start gap-2.5 mr-auto max-w-[95%]"
            >
              <div
                className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-mist"
                aria-hidden
              >
                <img
                  src={LOGO_ICON_SRC}
                  alt=""
                  className="h-3.5 w-3.5 object-contain"
                />
              </div>
              <div className="min-w-0 pt-0.5 text-sm leading-relaxed text-gray-900 whitespace-pre-wrap">
                {m.text}
              </div>
            </div>
          );
        })}
        {activity && (
          <div className="mr-auto flex items-center gap-2.5 text-xs text-gray-500">
            <span className="inline-flex h-6 w-6 shrink-0 items-center justify-center" aria-hidden>
              <span className="inline-block h-1.5 w-1.5 rounded-full bg-brand-primary animate-pulse" />
            </span>
            <span>{activity.label}</span>
          </div>
        )}
      </div>

      <form
        onSubmit={handleSend}
        className="flex items-center gap-2 border-t border-gray-100 p-2 bg-white"
      >
        <input
          className="flex-1 text-sm px-2.5 py-2 rounded-md border border-gray-200 outline-none focus:border-brand-primary"
          placeholder={
            status === "connected" ? "Ask about setup…" : "Connecting…"
          }
          value={input}
          disabled={status !== "connected"}
          onChange={(e) => setInput(e.target.value)}
        />
        <button
          type="submit"
          disabled={status !== "connected" || !input.trim()}
          className="p-2 rounded-md bg-brand-primary text-white hover:bg-brand-hover disabled:opacity-40"
          aria-label="Send"
        >
          <Send className="w-4 h-4" />
        </button>
      </form>

      <div className="flex items-center justify-between gap-2 border-t border-gray-100 px-3 py-1.5 bg-slate-50/80">
        <span className="text-[11px] text-gray-500">Was this helpful?</span>
        <div className="flex items-center gap-0.5">
          <button
            type="button"
            aria-label="Thumbs up"
            aria-pressed={sessionFeedback === "up"}
            onClick={() => handleSessionFeedback("up")}
            className={`p-1.5 rounded transition-colors ${
              sessionFeedback === "up"
                ? "text-green-600 bg-green-50"
                : "text-gray-400 hover:text-gray-600 hover:bg-white"
            }`}
          >
            <ThumbsUp className="w-3.5 h-3.5" />
          </button>
          <button
            type="button"
            aria-label="Thumbs down"
            aria-pressed={sessionFeedback === "down"}
            onClick={() => handleSessionFeedback("down")}
            className={`p-1.5 rounded transition-colors ${
              sessionFeedback === "down"
                ? "text-red-600 bg-red-50"
                : "text-gray-400 hover:text-gray-600 hover:bg-white"
            }`}
          >
            <ThumbsDown className="w-3.5 h-3.5" />
          </button>
        </div>
      </div>
    </div>
  );
}

function AssistantShell({ children }) {
  const [enabled] = useState(() => isAssistantEnabled());
  const [hidden, setHidden] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [panelOpen, setPanelOpen] = useState(false);
  const [editRouteDwellMet, setEditRouteDwellMet] = useState(false);
  // null = still checking; then { available, credit_status }
  const [availability, setAvailability] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const bridge = useAssistantUiBridge();
  const sendContextualUpdateRef = useRef(null);
  const sendUserMessageRef = useRef(null);
  const pendingNudgeRef = useRef(null);
  const pendingSessionExtrasRef = useRef(null);
  const depsRef = useRef({});
  const freshCheckInFlightRef = useRef(false);
  const outOfCreditsReportedRef = useRef(false);

  depsRef.current = {
    navigate,
    bridge,
    locationPathname: location.pathname,
  };

  const clientTools = useMemo(
    () => wrapClientToolsWithStatus(createClientTools(() => depsRef.current)),
    []
  );

  const reportOutOfCredits = useCallback((where, creditStatus) => {
    if (outOfCreditsReportedRef.current) return;
    outOfCreditsReportedRef.current = true;
    captureAssistantEvent("assistant_error", {
      message: "Out of Credits",
      credit_status: creditStatus || "exhausted",
      where,
    });
  }, []);

  const applyAvailability = useCallback(
    (result, where) => {
      setAvailability(result);
      if (isCreditsExhausted(result)) {
        reportOutOfCredits(where, result.credit_status);
      }
    },
    [reportOutOfCredits]
  );

  useEffect(() => {
    if (!enabled) return undefined;
    installAssistantAvailabilityDebugHelpers();
    let cancelled = false;
    (async () => {
      const result = await fetchAssistantAvailable();
      if (cancelled) return;
      applyAvailability(result, "availability_check");
    })();

    const onOverride = (ev) => {
      const detail = ev?.detail;
      if (!detail) return;
      applyAvailability(
        {
          available: detail.available === true,
          credit_status: detail.credit_status || "unknown",
        },
        detail.where || "console_override"
      );
      if (detail.available === false) {
        setPanelOpen(true);
      }
    };
    window.addEventListener(
      window.__twilanswerAssistantAvailabilityEvent ||
        "twilanswer-assistant-availability-override",
      onOverride
    );

    return () => {
      cancelled = true;
      window.removeEventListener(
        window.__twilanswerAssistantAvailabilityEvent ||
          "twilanswer-assistant-availability-override",
        onOverride
      );
    };
  }, [enabled, applyAvailability]);

  const handleConversationFailure = useCallback(
    async ({ where, message } = {}) => {
      captureAssistantEvent("assistant_error", {
        message: message || "ElevenLabs conversation error",
        where: where || "conversation",
      });
      if (freshCheckInFlightRef.current) return;
      freshCheckInFlightRef.current = true;
      try {
        const result = await fetchAssistantAvailable({ fresh: true });
        applyAvailability(result, `${where || "conversation"}_fresh_check`);
        if (!result.available) {
          setPanelOpen(true);
        }
      } finally {
        freshCheckInFlightRef.current = false;
      }
    },
    [applyAvailability]
  );

  const onTrial = isOrgOnTrial();
  const onCreateRoute = isCreateRoutePath(location.pathname);
  const onEditRoute = isEditRoutePath(location.pathname);
  const availabilityKnown = availability != null;
  const elevenLabsAvailable = availability?.available === true;

  useEffect(() => {
    if (!onEditRoute) {
      setEditRouteDwellMet(false);
      return undefined;
    }
    setEditRouteDwellMet(false);
    const id = setTimeout(() => setEditRouteDwellMet(true), EDIT_ROUTE_DWELL_MS);
    return () => clearTimeout(id);
  }, [onEditRoute, location.pathname]);

  const showFloatingLauncher =
    enabled &&
    availabilityKnown &&
    !hidden &&
    (onTrial || onCreateRoute || (onEditRoute && editRouteDwellMet));

  const setHiddenPersist = useCallback((next) => {
    setHidden(next);
    try {
      localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
    } catch {
      /* ignore */
    }
  }, []);

  const openAssistant = useCallback(() => {
    captureAssistantEvent("assistant_launcher_clicked", {
      source: "menu",
      available: availability?.available === true,
      credit_status: availability?.credit_status || null,
    });
    setPanelOpen(true);
  }, [availability]);

  const ACTIONABLE_EVENTS = useMemo(
    () =>
      new Set([
        "recording_added",
        "channel_added",
        "route_saved",
        "user_invited",
        "call_ended",
        "support_sent",
        "post_onboarding_inbound",
      ]),
    []
  );

  const pushContextual = useCallback(
    (event) => {
      if (!elevenLabsAvailable) return;
      const text = formatAssistantEvent(event);
      if (!text) return;

      const indicator = formatSystemIndicator(event);
      if (indicator) emitSystemIndicator(indicator);

      // sendContextualUpdate is passive (no agent turn). For actionable app events,
      // nudge with sendUserMessage so the agent replies immediately.
      const actionable = ACTIONABLE_EVENTS.has(event?.type);
      const nudge = sendUserMessageRef.current;
      const sendCtx = sendContextualUpdateRef.current;

      captureAssistantEvent("assistant_contextual_update", {
        event_type: event?.type || "unknown",
        actionable,
        mode: actionable ? "nudge" : "passive",
        path: event?.path || null,
        recording_id: event?.recordingId || null,
        route_id: event?.routeId || null,
        duration_seconds:
          event?.durationSeconds != null
            ? Math.round(Number(event.durationSeconds))
            : null,
      });

      try {
        if (actionable && typeof nudge === "function") {
          emitAssistantStatus({ phase: "thinking", label: "Thinking…" });
          nudge(text);
        } else if (typeof sendCtx === "function") {
          sendCtx(text);
        }
      } catch {
        /* ignore */
      }
    },
    [ACTIONABLE_EVENTS, elevenLabsAvailable]
  );

  const flushPendingNudge = useCallback(() => {
    const event = pendingNudgeRef.current;
    if (!event || !elevenLabsAvailable) return;
    if (typeof sendUserMessageRef.current !== "function") return;
    pendingNudgeRef.current = null;
    pushContextual(event);
  }, [elevenLabsAvailable, pushContextual]);

  const consumeSessionExtras = useCallback(() => {
    const extras = pendingSessionExtrasRef.current;
    pendingSessionExtrasRef.current = null;
    return extras;
  }, []);

  const openAssistantWithNudge = useCallback(
    (event) => {
      const nudgeEvent = event || { type: "post_onboarding_inbound" };
      const extras = buildSessionExtrasForEntryIntent(nudgeEvent.type);
      captureAssistantEvent("assistant_launcher_clicked", {
        source: "post_onboarding_nudge",
        available: availability?.available === true,
        credit_status: availability?.credit_status || null,
        nudge_type: nudgeEvent.type || null,
        entry_intent: extras?.entryIntent || null,
      });
      if (
        panelOpen &&
        elevenLabsAvailable &&
        typeof sendUserMessageRef.current === "function"
      ) {
        // Session already running — can't change first_message; just nudge.
        pushContextual(nudgeEvent);
        return;
      }
      pendingSessionExtrasRef.current = extras;
      // Always queue the entry event: actionable nudge, or passive context if
      // skipActionableNudge (custom first_message already covers the greeting).
      pendingNudgeRef.current = nudgeEvent;
      setPanelOpen(true);
    },
    [availability, elevenLabsAvailable, panelOpen, pushContextual]
  );

  const handlePanelReady = useCallback(
    ({ path, skipPanelOpenedNudge, skipActionableNudge } = {}) => {
      const pathname = path || location.pathname;
      if (!skipPanelOpenedNudge) {
        const { page_label } = describeDashboardPath(pathname);
        pushContextual({
          type: "panel_opened",
          path: pathname,
          pageLabel: page_label,
        });
      }
      if (skipActionableNudge) {
        // Custom first_message already greeted the user — pass setup context
        // passively so the agent can continue without a second spoken turn.
        const event = pendingNudgeRef.current;
        pendingNudgeRef.current = null;
        if (event && elevenLabsAvailable) {
          const text = formatAssistantEvent(event);
          const sendCtx = sendContextualUpdateRef.current;
          if (text && typeof sendCtx === "function") {
            try {
              sendCtx(text);
            } catch {
              /* ignore */
            }
          }
        }
      } else {
        flushPendingNudge();
      }
    },
    [
      location.pathname,
      pushContextual,
      flushPendingNudge,
      elevenLabsAvailable,
    ]
  );

  // Location → contextual update
  const prevPathRef = useRef(location.pathname);
  useEffect(() => {
    if (prevPathRef.current === location.pathname) return;
    prevPathRef.current = location.pathname;
    if (panelOpen) {
      pushContextual({ type: "navigation", path: location.pathname });
    }
  }, [location.pathname, panelOpen, pushContextual]);

  // Bridge events
  useEffect(() => {
    if (!bridge) return undefined;
    const handler = (event) => {
      if (panelOpen) pushContextual(event);
    };
    // Bridge emits via onAssistantEvent prop wired below in provider tree
    window.__twilanswerAssistantPush = handler;
    return () => {
      if (window.__twilanswerAssistantPush === handler) {
        delete window.__twilanswerAssistantPush;
      }
    };
  }, [bridge, panelOpen, pushContextual]);

  const ctx = useMemo(
    () => ({
      enabled,
      available: elevenLabsAvailable,
      creditStatus: availability?.credit_status || null,
      hidden,
      panelOpen,
      setPanelOpen,
      openAssistant,
      openAssistantWithNudge,
      setHiddenPersist,
      pushContextual,
      showLauncher: showFloatingLauncher,
    }),
    [
      enabled,
      elevenLabsAvailable,
      availability?.credit_status,
      hidden,
      panelOpen,
      openAssistant,
      openAssistantWithNudge,
      setHiddenPersist,
      pushContextual,
      showFloatingLauncher,
    ]
  );

  if (!enabled) {
    return (
      <OnboardingAssistantContext.Provider value={ctx}>
        {children}
      </OnboardingAssistantContext.Provider>
    );
  }

  // Credits exhausted / service down — keep launcher, but never start ElevenLabs.
  if (availabilityKnown && !elevenLabsAvailable) {
    return (
      <OnboardingAssistantContext.Provider value={ctx}>
        {children}
        <UnavailablePanel
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          creditStatus={availability?.credit_status}
        />
        {showFloatingLauncher && (
          <FloatingLauncherButton
            onClick={() => {
              captureAssistantEvent("assistant_launcher_clicked", {
                source: "floating",
                available: false,
                credit_status: availability?.credit_status || null,
              });
              setPanelOpen(true);
            }}
          />
        )}
        {hidden && (
          <button
            type="button"
            className="fixed bottom-4 right-4 z-[1390] text-xs text-gray-500 underline bg-white/90 px-2 py-1 rounded"
            onClick={() => {
              captureAssistantEvent("assistant_unhidden");
              setHiddenPersist(false);
            }}
          >
            Show setup assistant
          </button>
        )}
      </OnboardingAssistantContext.Provider>
    );
  }

  // Still checking availability — render children only (no launcher flash).
  if (!availabilityKnown) {
    return (
      <OnboardingAssistantContext.Provider value={ctx}>
        {children}
      </OnboardingAssistantContext.Provider>
    );
  }

  // Keep children outside ConversationProvider so the dashboard is not
  // remounted when availability resolves.
  return (
    <OnboardingAssistantContext.Provider value={ctx}>
      {children}
      <ConversationProvider
        textOnly
        clientTools={clientTools}
        onConnect={() => {}}
        onDisconnect={() => {}}
        onError={(err) => {
          void handleConversationFailure({
            where: "conversation",
            message: err?.message || String(err),
          });
        }}
        onMessage={(payload) => {
          const { message, source, event_id: eventId } = payload || {};
          if (source === "ai" && message) {
            window.dispatchEvent(
              new CustomEvent(MESSAGE_EVENT, {
                detail: { text: message, eventId: eventId ?? null },
              })
            );
          }
        }}
        onAgentTyping={(evt) => {
          const isTyping = evt?.is_typing ?? evt?.typing ?? true;
          if (isTyping) {
            emitAssistantStatus({ phase: "thinking", label: "Thinking…" });
          }
        }}
      >
        <ContextualUpdateBinder
          sendRef={sendContextualUpdateRef}
          nudgeRef={sendUserMessageRef}
        />
        <ChatPanelInner
          open={panelOpen}
          onClose={() => setPanelOpen(false)}
          onMinimize={() => {
            setPanelOpen(false);
          }}
          onConversationFailure={handleConversationFailure}
          onPanelReady={handlePanelReady}
          consumeSessionExtras={consumeSessionExtras}
        />
        {showFloatingLauncher && (
          <FloatingLauncherButton
            onClick={() => {
              captureAssistantEvent("assistant_launcher_clicked", {
                source: "floating",
                available: true,
                credit_status: availability?.credit_status || null,
              });
              setPanelOpen(true);
            }}
          />
        )}
        {hidden && (
          <button
            type="button"
            className="fixed bottom-4 right-4 z-[1390] text-xs text-gray-500 underline bg-white/90 px-2 py-1 rounded"
            onClick={() => {
              captureAssistantEvent("assistant_unhidden");
              setHiddenPersist(false);
            }}
          >
            Show setup assistant
          </button>
        )}
      </ConversationProvider>
    </OnboardingAssistantContext.Provider>
  );
}

function ContextualUpdateBinder({ sendRef, nudgeRef }) {
  const { sendContextualUpdate, sendUserMessage } = useConversationControls();
  useEffect(() => {
    sendRef.current = sendContextualUpdate;
    if (nudgeRef) nudgeRef.current = sendUserMessage;
    return () => {
      sendRef.current = null;
      if (nudgeRef) nudgeRef.current = null;
    };
  }, [sendContextualUpdate, sendUserMessage, sendRef, nudgeRef]);
  return null;
}

/**
 * Wraps dashboard content. Must sit under Router and AssistantUiBridgeProvider.
 */
export function OnboardingAssistantProvider({ children }) {
  return <AssistantShell>{children}</AssistantShell>;
}

export function useOnboardingAssistant() {
  return useContext(OnboardingAssistantContext);
}

/** Forward bridge events into the assistant contextual channel */
export function forwardAssistantBridgeEvent(event) {
  if (typeof window !== "undefined" && window.__twilanswerAssistantPush) {
    window.__twilanswerAssistantPush(event);
  }
}
